import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useEditGate } from '../../components/confirm';
import { AppToolbar, EditGateActions } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import '../backlog/backlog.css';

// ─────────────────────────────────────────────────────────────────────────────
// Settings → Default Settings → App Config
//
// ALPACA's own application settings, ported from the legacy XAF form
// (Modules/BusinessObjects/Setting/AppConfig.cs).  Same layout and field order
// as the legacy screen so anyone moving between the two products recognises it.
//
// Backed by the SHARED single-row dbo.AppConfig that ALPACA reads at runtime —
// a change saved here takes effect in ALPACA too (CLAUDE.md §5.0: never shadow
// legacy data with a second copy).
//
// SECRETS ARE WRITE-ONLY.  The legacy form prints the SQL password, the mail
// password and the AI keys in clear text on screen.  Reproducing that would
// serve live credentials to every admin's browser (and its cache, and any proxy
// log), so here each secret shows only whether it is set, with a Change button
// to replace it.  Leaving one alone keeps the stored value; clearing the box and
// saving erases it.  §18.12 / audit item C1.
// ─────────────────────────────────────────────────────────────────────────────

interface Cfg {
  oid: string;
  dateFormat: string | null;
  traceLogLocation: string | null;
  finalReportPath: string | null;
  allowPrint: boolean;
  messageTimer: string | null;
  projectName: string | null;
  getLocationByIpAddressApiUrl: string | null;
  ssoLoginUrl: string | null;
  ssoLogoutUrl: string | null;
  helpCenterUrl: string | null;
  salesAnalysisSiteUrl: string | null;
  productName: string | null;
  reportWaterMarkText: string | null;
  intelligenceSiteUrl: string | null;
  mailFromUserName: string | null;
  mailPort: number | null;
  g6Enabled: boolean;
  avaEnabled: boolean;
  avaAiProvider: string | null;
  helpCenterConnectionStringSet: boolean;
  salesAnalysisSiteConnectionStringSet: boolean;
  mailFromPasswordSet: boolean;
  avaAnthropicApiKeySet: boolean;
  avaGeminiApiKeySet: boolean;
}

/** The five write-only fields, keyed by the property the API expects. */
type SecretKey =
  | 'helpCenterConnectionString' | 'salesAnalysisSiteConnectionString'
  | 'mailFromPassword' | 'avaAnthropicApiKey' | 'avaGeminiApiKey';

const LABEL: React.CSSProperties = {
  fontSize: 12.5, color: 'var(--ai-ink)', fontWeight: 600,
  minWidth: 220, flexShrink: 0, paddingTop: 6,
};
const INPUT: React.CSSProperties = {
  flex: 1, minWidth: 0, fontSize: 12.5, padding: '5px 8px',
  border: '1px solid var(--ai-line)', borderRadius: 4, color: 'var(--ai-ink)',
};

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '5px 0' }}>
      <div style={LABEL}>
        {label}
        {hint && <div style={{ fontSize: 11, color: 'var(--ai-muted)', fontWeight: 400 }}>{hint}</div>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{
        fontSize: 14, fontWeight: 700, color: 'var(--ai-ink)',
        borderBottom: '1px solid var(--ai-line)', paddingBottom: 6, marginBottom: 10,
      }}>{title}</div>
      {children}
    </div>
  );
}

export default function AlpacaAppConfigPage() {
  const notify = useNotify();
  const gate = useEditGate();
  const ro = gate.readOnly;

  const [cfg, setCfg] = useState<Cfg | null>(null);
  const [form, setForm] = useState<Partial<Cfg>>({});
  // Secrets typed THIS edit. Absent = untouched (server keeps the stored value).
  const [secrets, setSecrets] = useState<Partial<Record<SecretKey, string>>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await api.get<{ success: boolean; data: Cfg | null }>('/api/v1/alpaca-app-config');
      if (res.data.success && res.data.data) { setCfg(res.data.data); setForm(res.data.data); }
      else notify.error('ALPACA’s App Config row was not found.');
    } catch { notify.error('Could not load App Config.'); }
    finally { setLoadMs(performance.now() - t0); setLoading(false); }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  const set = <K extends keyof Cfg>(k: K, v: Cfg[K]) => setForm(f => ({ ...f, [k]: v }));

  const handleCancel = () => { if (cfg) setForm(cfg); setSecrets({}); };

  const handleSave = () => {
    setSaving(true);
    // Through the SHARED save flow (standing rule 7 / §18.6): confirm dialog →
    // reason-for-change gate when "App Config" has ReasonRequired on → the
    // audited PUT → "Saved successfully." → back to read-only. Calling the PUT
    // directly would skip all four.
    void gate.save({
      formName: 'App Config',
      successMessage: 'App Config saved successfully.',
      // Only secrets the admin actually typed are sent; the rest are omitted so
      // the server keeps the stored value.
      save: async () => {
        await api.put('/api/v1/alpaca-app-config', { ...form, ...secrets });
        setSecrets({});
      },
      reload: load,
      onError: (e: unknown) => {
        const status = (e as { response?: { status?: number } })?.response?.status;
        notify.error(status === 403
          ? 'Administrator / service access required to change App Config.'
          : 'Save failed. Please try again.');
      },
    }).finally(() => setSaving(false));
  };

  /** A write-only credential: shows whether it is set, never the value. */
  const Secret = ({ label, k, isSet }: { label: string; k: SecretKey; isSet: boolean }) => {
    const typed = secrets[k];
    return (
      <Field label={label}>
        {ro ? (
          <span className={`bl-pill ${isSet ? 'ok' : 'info'}`} style={{ fontSize: 11.5 }}>
            {isSet ? '•••••••• set' : 'not set'}
          </span>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="password" autoComplete="new-password" style={INPUT}
              placeholder={isSet ? '•••••••• — leave blank to keep' : 'not set'}
              value={typed ?? ''}
              onChange={(e) => setSecrets(s => ({ ...s, [k]: e.target.value }))}
            />
            {typed !== undefined && (
              <button className="ca-btn" title="Leave this credential unchanged"
                onClick={() => setSecrets(s => { const n = { ...s }; delete n[k]; return n; })}>
                Undo
              </button>
            )}
          </div>
        )}
      </Field>
    );
  };

  const Text = ({ label, k, hint }: { label: string; k: keyof Cfg; hint?: string }) => (
    <Field label={label} hint={hint}>
      <input type="text" style={INPUT} disabled={ro}
        value={(form[k] as string | null) ?? ''}
        onChange={(e) => set(k, (e.target.value || null) as never)} />
    </Field>
  );

  const Check = ({ label, k }: { label: string; k: keyof Cfg }) => (
    <Field label={label}>
      <input type="checkbox" disabled={ro} checked={!!form[k]}
        onChange={(e) => set(k, e.target.checked as never)} />
    </Field>
  );

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Settings</span><i className="ti ti-chevron-right" />
            <span>Default Settings</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">App Config</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="alpacaappconfig" auditFormName="App Config"
              loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">
            <i className="ti ti-info-circle" style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 4 }} />
            ALPACA’s shared application settings — a change here takes effect in ALPACA too.
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={<EditGateActions gate={gate} saving={saving} disabled={loading}
              onSave={handleSave} onCancel={handleCancel} />}
            onRefresh={() => void load()} refreshing={loading} refreshDisabled={gate.editing}
          />
        </div>

        <div className="bl-card bl-card-flex">
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 28px 32px' }}>
            <div style={{ maxWidth: 900 }}>

              <Section title="App Settings">
                <Text  label="Date Format:"                       k="dateFormat" />
                <Text  label="Final Report Path:"                 k="finalReportPath" />
                <Text  label="Project Name:"                      k="projectName" />
                <Text  label="Get Location By IP Address API URL:" k="getLocationByIpAddressApiUrl" />
                <Text  label="Product Name:"                      k="productName" />
                <Text  label="Help Center URL:"                   k="helpCenterUrl" />
                <Secret label="Help Center Connection String:"    k="helpCenterConnectionString"
                        isSet={!!cfg?.helpCenterConnectionStringSet} />
                <Text  label="SSO Login URL:"                     k="ssoLoginUrl" />
                <Text  label="SSO Logout URL:"                    k="ssoLogoutUrl" />
                <Text  label="Trace Log Location:"                k="traceLogLocation" />
                <Text  label="Message Timer:"                     k="messageTimer" />
                <Check label="Allow Print"                        k="allowPrint" />
                <Text  label="Report Water Mark Text:"            k="reportWaterMarkText" />
                <Text  label="Sales Analysis Site URL:"           k="salesAnalysisSiteUrl" />
                <Secret label="Sales Analysis Site Connection String:" k="salesAnalysisSiteConnectionString"
                        isSet={!!cfg?.salesAnalysisSiteConnectionStringSet} />
                <Text  label="Alpaca Intelligence URL:"           k="intelligenceSiteUrl"
                       hint="Opened by the G6 Intelligence header link" />
              </Section>

              <Section title="Email Settings">
                <Text   label="Mail From User Name:" k="mailFromUserName" />
                <Secret label="Mail From Password:"  k="mailFromPassword"
                        isSet={!!cfg?.mailFromPasswordSet} />
                <Field label="Mail Port:">
                  <input type="number" min={0} max={65535} style={{ ...INPUT, maxWidth: 140 }} disabled={ro}
                    value={form.mailPort ?? ''}
                    onChange={(e) => set('mailPort', (e.target.value === '' ? null : Number(e.target.value)) as never)} />
                </Field>
                <Secret label="AVA Anthropic API Key:" k="avaAnthropicApiKey"
                        isSet={!!cfg?.avaAnthropicApiKeySet} />
                <Secret label="AVA Gemini API Key:"    k="avaGeminiApiKey"
                        isSet={!!cfg?.avaGeminiApiKeySet} />
                <Text   label="AVA AI Provider:"       k="avaAiProvider" />
                <Check  label="AVA Enabled"            k="avaEnabled" />
                <Check  label="G6 Enable"              k="g6Enabled" />
              </Section>

              {!ro && (
                <div style={{
                  fontSize: 11.5, color: 'var(--ai-muted)', background: 'var(--ai-header-bg)',
                  border: '1px solid var(--ai-line)', borderRadius: 5, padding: '8px 11px', maxWidth: 620,
                }}>
                  <i className="ti ti-shield-lock" style={{ marginRight: 5 }} />
                  Passwords, connection strings and API keys are never shown. Leave one blank to keep
                  the stored value; type a new one to replace it.
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
